// ============================================
// Character Editor — game-side state machine
// Extracted from main.js
// (The old standalone editor.html/editor.css PoC was retired 0.3.1 —
// it had drifted 5 phases behind this file and never shipped.)
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { fetchStoryJson, renderLoadFailureCard } from '../engine/story-fetch.js';
import { variableManager } from '../engine/variables.js';

import { saveSystem } from '../systems/save.js';
import { registerUndoStateProviders } from '../systems/game-loader.js';
import { inventoryManager } from '../systems/inventory.js';
import { appearanceState } from '../systems/appearance.js';
import { achievementManager } from '../systems/achievements.js';
import { statisticsTracker } from '../systems/statistics.js';
import { L } from '../systems/localization.js';

import { layoutManager } from './layout.js';
import { choicesUI } from './choices.js';
import { phoneUI } from './phone.js';
import { shopUI } from './shop.js';
import { statsUI } from './stats.js';
import { wardrobeUI } from './wardrobe.js';
import { characterPanelUI } from './character-panel.js';
import { galleryUI } from './gallery.js';
import { sanitizeName } from '../utils/escape.js';

import { BACKGROUNDS, TRAITS, SYNERGIES, PERKS, PERK_CATEGORIES, INTENSITIES, MASC_PATH_OPTIONS, ORIENTATION_LEAN_OPTIONS, MASC_ROUTE_ENABLED, PARTNER_OPTIONS, ARCHETYPES, V031_CONTENT_ENABLED } from './editor-data.js';
import { CONTENT_TRACKS, CONTENT_PREFS_ENABLED, buildContentPrefCards, hasVisibleContentTracks } from './content-tracks.js';
import { isPat } from '../engine/pgate.js';
import { tutorialTips } from './tutorial-tips.js';

// Inline to avoid import error if editor-data.js is cached without this export
const PERK_CATEGORY_KEYS = {
    'Stat': 'perk.cat.stat', 'Gameplay': 'perk.cat.gameplay',
    'Social': 'perk.cat.social', 'Streaming': 'perk.cat.streaming',
};

// ── Private module state ──
// SINGLE factory for the default editor state. resetState(), quickStart() and
// the module init all build from here — pre-0.3.1 they were three drifting
// literals and BOTH reset paths dropped `contentPrefs`, so after any editor
// re-entry the pref toggles mutated a stale array and pref_* writes silently
// stopped (workflow audit 2026-07-15). Add new fields HERE, nowhere else.
function _defaultEditorState() {
    return {
        phase: 0,
        selectedBackground: null,
        selectedTraits: [],
        selectedPerks: [],
        perkPoints: 0, // From unlocked gameplay achievements
        perkTab: 'perks', // active perk-step tab: 'perks' | 'handicaps'
        selectedIntensity: 1, // Default: Roommate (soft, all endings available, recommended for first-time players)
        partner: 'girlfriend', // PARTNER_OPTIONS id: 'girlfriend' (legacy default) | 'boyfriend' (0.3.1, gated) | 'none'
        mascPath: 0,            // MASC_ROUTE (0.2.8): 0=feminize (default), 1=stay/reclaim masculine. Maps to Ink masc_path. Card hidden unless MASC_ROUTE_ENABLED.
        orientationLean: 0,     // MASC_ROUTE (0.2.8): -3=men, +3=women, 0=bi/open (default). Maps to Ink orientation_lean.
        contentPrefs: [],       // CONTENT_TOGGLES (0.3): opted-in track ids. Maps to Ink pref_<id>. NEW characters default every track OFF (opt-in, same as BNWO — owner call 2026-07-23). Existing saves never run this loop, so they keep the Ink VAR defaults: pref_pet_play=true grandfathers players who already had the Kennel, pref_free_use=false because Free Use is new content nobody has played. Dormant unless CONTENT_PREFS_ENABLED.
        pacingTargetDays: 60,   // Pacing 2.0 slider value — synced from #pacing-slider input + archetype picks so getState()/presets see it (pre-0.3.1 the slider bypassed editorState entirely).
        archetypeId: null,      // Archetype quick-start: preset id this run was seeded from (null = hand-built). Recap chips re-derive from editorState, NEVER from this.
    };
}
let editorState = _defaultEditorState();

// Boyfriend card gate: JS mirror const OR the localStorage preview flag
// (test/QA hook, feedback_patreon_qa_test_technique pattern — a reload wipes
// nothing here, the flag is explicit opt-in dev tooling).
function _v031On() {
    if (V031_CONTENT_ENABLED) return true;
    try { return localStorage.getItem('PnO_V031Preview') === 'true'; } catch (e) { return false; }
}

/**
 * Callbacks provided by main.js at init time:
 *   setCharacterData(data) - sets App.characterData
 *   getCharacterData() - reads App.characterData
 *   updateCharacterPanelSlots() - refreshes equipment slot images
 *   getVersionInfo() - returns VERSION_INFO object
 */
let _opts = {};

// ── Helper functions (private) ──

// Build a card-thumb markup that works both in the built zip (where
// images are converted to .webp at build time) and in the dev tree
// (where only .png exists in the source). The <img> tag swaps to .png
// if the .webp 404s.
function _thumbHtml(relPath) {
    return `<div class="card-thumb"><img class="card-thumb-img" src="assets/images/${relPath}.webp" alt="" loading="lazy" onerror="this.onerror=null;this.src='assets/images/${relPath}.png'"></div>`;
}

function createCard(data, type) {
    const card = document.createElement('div');
    card.className = 'card' + (data.thumb ? ' card-with-thumb' : '');
    const thumbHtml = data.thumb ? _thumbHtml(data.thumb) : '';
    const iconHtml = data.icon && data.icon.includes('/')
        ? `<img src="${data.icon}" alt="${data.name}" width="48" height="48">`
        : data.icon;
    // L10n: background cards use "bg.{id}.name" etc., trait cards use "trait.{id}.name" etc.
    const l10nPrefix = type === 'background' ? `bg.${data.l10nKey || data.id}` : `trait.${data.id}`;
    const localName = L.get(`${l10nPrefix}.name`);
    const localDesc = L.get(`${l10nPrefix}.desc`);
    const localBonus = L.get(`${l10nPrefix}.bonus`);
    const displayName = localName !== `${l10nPrefix}.name` ? localName : data.name;
    const displayDesc = localDesc !== `${l10nPrefix}.desc` ? localDesc : data.desc;
    const displayBonus = localBonus !== `${l10nPrefix}.bonus` ? localBonus : data.bonus;
    // Pacing-note (only on trait cards that affect pacing, Curious + Devoted)
    let pacingNoteText = data.pacingNote;
    if (type === 'trait' && data.pacingNote) {
        const noteKey = `trait.${data.id}.pacing_note`;
        const localNote = L.get(noteKey);
        if (localNote !== noteKey) pacingNoteText = localNote;
    }
    const pacingNoteHtml = (type === 'trait' && data.pacingNote)
        ? `<div class="card-pacing-note pacing-${data.pacing}">⏵ ${pacingNoteText}</div>`
        : '';
    // Audit D31: Recommended-badge on cards that carry `recommended: true`
    // in editor-data.js. Was only rendered on intensity cards in this file's
    // intensity-section path; bring the same affordance up to backgrounds
    // (Gym Bro) and traits when they're flagged as recommended.
    const recommendedLabel = L.get('editor.recommended');
    const recommendedText = recommendedLabel !== 'editor.recommended' ? recommendedLabel : 'Recommended';
    const recommendedHtml = data.recommended
        ? `<span class="card-recommended">${recommendedText}</span>`
        : '';
    card.innerHTML = `
        ${thumbHtml}
        <span class="card-check">&#10003;</span>
        ${recommendedHtml}
        <div class="card-icon">${iconHtml}</div>
        <div class="card-name">${displayName}</div>
        <div class="card-desc">${displayDesc}</div>
        <div class="card-bonus">${displayBonus}</div>
        ${pacingNoteHtml}
    `;

    if (type === 'background') {
        card.addEventListener('click', () => selectBackground(data.id, card));
        _wireCardA11y(card, 'radio');
    } else {
        card.addEventListener('click', () => selectTrait(data.id, card));
        _wireCardA11y(card, 'checkbox');
    }

    return card;
}

// Background is the first phase — gate its Continue exactly like the Traits
// phase so an empty tap isn't a silent dead-click.
function _setBackgroundGate() {
    const btn = document.querySelector('#phase-background .btn-next');
    if (!btn) return;
    const ready = !!editorState.selectedBackground;
    btn.disabled = !ready;
    if (ready) btn.removeAttribute('aria-disabled');
    else btn.setAttribute('aria-disabled', 'true');
}

function selectBackground(id, card) {
    editorState.selectedBackground = id;
    document.querySelectorAll('#background-grid .card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    _setBackgroundGate();
    updateDifficulty();
    _updateMascRecommended();
}

// MASC_ROUTE §8: the Recommended badge on the "How do you see yourself?" cards
// follows the picked background — Gym Bro (3) / Military (7) already start with
// fem-gain resistance, so they suggest the masculine pick; every other
// background keeps the feminine default. Re-evaluated on background selection.
function _updateMascRecommended() {
    const grid = document.getElementById('masc-path-grid');
    if (!grid || !grid.children.length) return;
    const recommendedLabel = L.get('editor.recommended');
    const recommendedText = recommendedLabel !== 'editor.recommended' ? recommendedLabel : 'Recommended';
    const recId = (editorState.selectedBackground === 3 || editorState.selectedBackground === 7) ? 1 : 0;
    grid.querySelectorAll('.card-recommended').forEach(el => el.remove());
    const target = grid.querySelector(`.card[data-masc-id="${recId}"]`);
    if (target) {
        const span = document.createElement('span');
        span.className = 'card-recommended';
        span.textContent = recommendedText;
        target.appendChild(span);
    }
}

// Pacing 2.0 story-length slider (PACING_RETUNE.md). Zone by day count:
// 35-45 Fast, 50-70 Normal (default 60), 75-90 Slow. Purely cosmetic labels;
// the actual arc scaling is driven by pacing_target_days downstream.
function _pacingZone(days) {
    if (days <= 45) return { nameKey: 'editor.pacing_zone_fast',   name: 'Fast',
        descKey: 'editor.pacing_desc_fast',   desc: 'Speedrun a path. Escalation from day one.' };
    if (days <= 70) return { nameKey: 'editor.pacing_zone_normal', name: 'Normal',
        descKey: 'editor.pacing_desc_normal', desc: "A full run. You'll see most of the game before an ending opens. No rush." };
    return { nameKey: 'editor.pacing_zone_slow',   name: 'Slow',
        descKey: 'editor.pacing_desc_slow',   desc: 'A long, gentle transformation. Everything, spread out.' };
}
function _l10nOr(key, fallback) {
    try { const v = L.get(key); return (v && v !== key) ? v : fallback; } catch (e) { return fallback; }
}
function _updatePacingSliderReadout(val) {
    const days = parseInt(val, 10) || 60;
    const z = _pacingZone(days);
    const set = (id, txt) => { const el = document.getElementById(id); if (el) el.textContent = txt; };
    set('pacing-slider-days', String(days));
    set('pacing-slider-weeks', '≈ ' + Math.round(days / 7) + ' ' + _l10nOr('editor.pacing_weeks', 'weeks'));
    set('pacing-slider-zone', _l10nOr(z.nameKey, z.name));
    set('pacing-slider-desc', _l10nOr(z.descKey, z.desc));
}

// MASC_ROUTE coupling — make the master_intensity=0 override VISIBLE instead of
// silently discarding the player's Roommate/Landlord/Owner pick at startNewGame
// (the line-809 override in startNewGame). Phase 1 builds masc content (3
// partners, construction job, masc ending) for the no-Master scenario only, so
// "Stay your own man" forces Independent. Two-way sync, called from BOTH the
// intensity and masc-path click handlers + once on render:
//   - masc route ON (mascPath >= 1)  → force Independent, grey the other
//     intensity cards (still clickable — clicking one cancels the masc route),
//     show the explanatory note.
//   - masc route OFF (mascPath == 0) → un-grey, hide the note, leave the
//     player's intensity pick intact.
// foundby:Maj (Discord) — picked Owner + "Stay your own man", kept landing on
// the Independent path with no feedback.
function _syncMascIntensityCoupling() {
    const mascOn = (editorState.mascPath ?? 0) >= 1;
    if (mascOn) editorState.selectedIntensity = 0;

    const intGrid = document.getElementById('intensity-grid');
    if (intGrid) {
        intGrid.querySelectorAll('.card').forEach(c => {
            const id = Number(c.dataset.intensityId);
            const locked = mascOn && id !== 0;
            c.classList.toggle('card-locked', locked);
            c.setAttribute('aria-disabled', locked ? 'true' : 'false');
            c.classList.toggle('selected', id === editorState.selectedIntensity);
        });
    }
    const mpGrid = document.getElementById('masc-path-grid');
    if (mpGrid) {
        mpGrid.querySelectorAll('.card').forEach(c =>
            c.classList.toggle('selected', Number(c.dataset.mascId) === (editorState.mascPath ?? 0)));
    }
    // Keep the intensity detail panel honest about the (possibly forced) pick.
    const detailEl = document.getElementById('intensity-detail');
    const curInt = INTENSITIES.find(i => i.id === editorState.selectedIntensity);
    if (detailEl && curInt) {
        const localDetail = L.get(`intensity.${curInt.key}.detail`);
        detailEl.textContent = localDetail !== `intensity.${curInt.key}.detail` ? localDetail : curInt.detail;
    }
    const note = document.getElementById('masc-intensity-note');
    if (note) note.style.display = mascOn ? '' : 'none';
}

// Boyfriend + lean-"Women" cross-talk (feedback_editor_crossfield_silent_override):
// a grey suggest-note on BOTH surfaces (wizard phase 2 + archetype confirm),
// never a forced value — the player's picks always win.
function _updatePartnerOrientationNote() {
    const show = editorState.partner === 'boyfriend' && editorState.orientationLean === 3;
    ['partner-orientation-note', 'archetype-partner-note'].forEach(nid => {
        const el = document.getElementById(nid);
        if (el) el.style.display = show ? '' : 'none';
    });
}

function selectTrait(id, card) {
    const idx = editorState.selectedTraits.indexOf(id);
    if (idx >= 0) {
        // Already selected → toggle off
        editorState.selectedTraits.splice(idx, 1);
        card.classList.remove('selected');
    } else if (editorState.selectedTraits.length < 2) {
        editorState.selectedTraits.push(id);
        card.classList.add('selected');
    } else {
        // Audit D24: was a no-op when 2 already picked. Per user direction
        // (2026-05-11: "2 picken"), pick 2 is mandatory and a click on a
        // 3rd trait now replaces the OLDEST selection. Less frustrating
        // than a silent dead click — user always reaches 2/2 by clicking.
        //
        // F6 (2026-05-26): add a brief flash on the dropped card so the
        // FIFO swap is visible — previously players couldn't see WHICH
        // trait got deselected.
        const dropped = editorState.selectedTraits.shift();
        const oldCard = document.querySelector(`#trait-grid .card[data-trait-id="${dropped}"]`);
        if (oldCard) {
            oldCard.classList.remove('selected');
            oldCard.classList.add('trait-dropped');
            setTimeout(() => oldCard.classList.remove('trait-dropped'), 500);
        }
        editorState.selectedTraits.push(id);
        card.classList.add('selected');
    }

    _repaintTraitsFromState();
    updateDifficulty();
}

// Repaint the trait grid (selection, conflict dimming, counter, Continue gate,
// synergy line) from editorState. Shared by selectTrait AND the archetype
// pre-fill path (applyDefaultSelections) so the two can't drift.
function _repaintTraitsFromState() {
    const allConflicts = new Set();
    editorState.selectedTraits.forEach(selId => {
        const t = TRAITS.find(x => x.id === selId);
        if (t && t.conflicts) t.conflicts.forEach(c => allConflicts.add(c));
    });

    document.querySelectorAll('#trait-grid .card').forEach(c => {
        const tid = c.dataset.traitId;
        c.classList.toggle('selected', editorState.selectedTraits.includes(tid));
        c.classList.toggle('dimmed', allConflicts.has(tid) && !editorState.selectedTraits.includes(tid));
    });

    updateSynergy();
    // Audit D24: counter + Continue-button gate. Pick 2 is mandatory.
    const counterNum = document.getElementById('trait-counter-num');
    const counter = document.getElementById('trait-counter');
    if (counterNum) counterNum.textContent = String(editorState.selectedTraits.length);
    if (counter) counter.classList.toggle('trait-counter-ready', editorState.selectedTraits.length === 2);
    const traitsPhase = document.getElementById('phase-traits');
    if (traitsPhase) {
        const continueBtn = traitsPhase.querySelector('.btn-next');
        if (continueBtn) {
            const ready = editorState.selectedTraits.length === 2;
            continueBtn.disabled = !ready;
            if (ready) continueBtn.removeAttribute('aria-disabled');
            else continueBtn.setAttribute('aria-disabled', 'true');
        }
    }
}

function updateSynergy() {
    const el = document.getElementById('synergy-text');
    if (!el) return;
    if (editorState.selectedTraits.length === 2) {
        const traits = [...editorState.selectedTraits].sort();
        const synergyKey = `synergy.${traits.join('_')}`;
        const localSynergy = L.get(synergyKey);
        const enFallback = SYNERGIES[traits.join('+')] || '';
        el.textContent = localSynergy !== synergyKey ? localSynergy : enFallback;
    } else {
        el.textContent = '';
    }
}

// Pure difficulty math shared by the wizard pills AND the archetype cards
// (same formula startNewGame pushes as difficulty_score, minus hardship
// diffWeights — presets never bundle hardships so card label == Ink score).
function _difficultyForPicks(bgId, traitIds, intensityId) {
    let score = 0;
    const bg = BACKGROUNDS.find(b => b.id === bgId);
    if (bg) score += bg.diff;
    (traitIds || []).forEach(id => {
        const t = TRAITS.find(x => x.id === id);
        if (t) score += t.diff;
    });
    const int = INTENSITIES.find(i => i.id === intensityId);
    if (int) score += int.diff;
    return score;
}
function _difficultyLabelFor(score) {
    if (score <= 0) return { label: L.get('editor.difficulty_easy'), cls: 'diff-easy' };
    if (score <= 2) return { label: L.get('editor.difficulty_normal'), cls: 'diff-normal' };
    if (score <= 4) return { label: L.get('editor.difficulty_hard'), cls: 'diff-hard' };
    return { label: L.get('editor.difficulty_challenge'), cls: 'diff-challenge' };
}

function updateDifficulty() {
    // There are two .difficulty readouts (Traits phase + Intensity phase).
    // getElementById would only ever hit the first; querySelectorAll updates
    // BOTH so the Intensity phase — where the biggest difficulty swing happens
    // (Independent -2 … Owner +2) — finally reflects the choice. (Was a
    // duplicate-id bug: the intensity pill stayed permanently blank.)
    const els = document.querySelectorAll('#screen-character-editor .difficulty');
    if (!els.length) return;

    const score = _difficultyForPicks(
        editorState.selectedBackground, editorState.selectedTraits, editorState.selectedIntensity);
    const { label, cls } = _difficultyLabelFor(score);

    els.forEach(el => {
        el.textContent = label;
        el.className = 'difficulty ' + cls;
    });
}

// ── Accessibility helpers ──────────────────────────────────────────────
// Cards are <div>s; wire each as a keyboard-operable radio/checkbox so the
// whole wizard is completable without a mouse. role: 'radio' (single-select
// grids) or 'checkbox' (trait/perk multi-select).
function _wireCardA11y(card, role) {
    card.setAttribute('tabindex', '0');
    card.setAttribute('role', role);
    card.setAttribute('aria-checked', card.classList.contains('selected') ? 'true' : 'false');
    card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            card.click();
        }
    });
    // Runs AFTER the select-listener (attached earlier) mutates .selected.
    card.addEventListener('click', () => _refreshAriaChecked());
}

// Sync aria-checked / aria-disabled from the visual .selected / .dimmed state
// across every editor grid. Cheap; called after any selection mutation.
function _refreshAriaChecked() {
    document.querySelectorAll('#screen-character-editor .card[role]').forEach(c => {
        c.setAttribute('aria-checked', c.classList.contains('selected') ? 'true' : 'false');
        if (c.classList.contains('dimmed')) c.setAttribute('aria-disabled', 'true');
        else c.removeAttribute('aria-disabled');
    });
}

// Phase order: 0=Background, 1=Traits, 2=Intensity+GF, 3=Names, 4=Perks, 5=Preview
function editorNextPhase() {
    // Validation per phase
    if (editorState.phase === 0 && !editorState.selectedBackground) return;
    // Audit D24 (2026-05-11, user direction "2 picken"): traits are back to
    // strictly 2. Continue gated until selectedTraits.length === 2. The
    // earlier U-02 0-2 mode was reverted per user feedback.
    if (editorState.phase === 1 && editorState.selectedTraits.length !== 2) return;

    // Build perk grid when entering perk phase (phase 4)
    if (editorState.phase === 3) buildPerkGrid();
    // Build preview when entering preview phase (phase 5)
    if (editorState.phase === 4) buildPreview();

    const phases = document.querySelectorAll('.phase');
    const dots = document.querySelectorAll('.progress-dots .dot');
    const oldPhase = editorState.phase;

    phases[oldPhase]?.classList.remove('active');
    phases[oldPhase]?.classList.add('phase-exit-left');
    editorState.phase = Math.min(editorState.phase + 1, phases.length - 1);

    // Update name field visibility when entering Names phase (phase 3)
    if (editorState.phase === 3) _updateNameFields();

    // Animate: old slides left out, new slides right in
    setTimeout(() => {
        phases[oldPhase]?.classList.remove('phase-exit-left');
        phases[editorState.phase]?.classList.add('active', 'phase-enter-right');
        // Stagger card fly-in
        _staggerCards(phases[editorState.phase]);
        _onPhaseShown(phases[editorState.phase]);
        setTimeout(() => phases[editorState.phase]?.classList.remove('phase-enter-right'), 350);
    }, 150);

    _updateDots(dots);
}

function editorPrevPhase() {
    const phases = document.querySelectorAll('.phase');
    const dots = document.querySelectorAll('.progress-dots .dot');
    const oldPhase = editorState.phase;

    phases[oldPhase]?.classList.remove('active');
    phases[oldPhase]?.classList.add('phase-exit-right');
    editorState.phase = Math.max(editorState.phase - 1, 0);

    setTimeout(() => {
        phases[oldPhase]?.classList.remove('phase-exit-right');
        phases[editorState.phase]?.classList.add('active', 'phase-enter-left');
        _staggerCards(phases[editorState.phase]);
        _onPhaseShown(phases[editorState.phase]);
        setTimeout(() => phases[editorState.phase]?.classList.remove('phase-enter-left'), 350);
    }, 150);

    _updateDots(dots);
}

function _updateDots(dots) {
    dots.forEach((dot, i) => {
        dot.classList.remove('active', 'done');
        if (i === editorState.phase) dot.classList.add('active');
        else if (i < editorState.phase) dot.classList.add('done');
    });
    // SR-only step announcement (dots are decorative/aria-hidden).
    const stepLabel = document.getElementById('editor-step-label');
    if (stepLabel) {
        const tmpl = L.get('editor.step_of');
        const total = dots.length || 6;
        const n = editorState.phase + 1;
        stepLabel.textContent = (tmpl && tmpl !== 'editor.step_of')
            ? tmpl.replace('{n}', n).replace('{total}', total)
            : `Step ${n} of ${total}`;
    }
}

// After a phase becomes visible: re-anchor scroll to the top and move focus to
// the new heading so keyboard/SR users know the panel changed and long phases
// don't open mid-page.
function _onPhaseShown(phaseEl) {
    const screen = document.getElementById('screen-character-editor');
    if (screen) screen.scrollTop = 0;
    try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
    if (!phaseEl) return;
    const heading = phaseEl.querySelector('.editor-title');
    if (heading) {
        heading.setAttribute('tabindex', '-1');
        // Focus without yanking the page (scroll already reset above).
        try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
    }
}

function _staggerCards(phaseEl) {
    if (!phaseEl) return;
    const cards = phaseEl.querySelectorAll('.card, .field');
    cards.forEach((card, i) => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        setTimeout(() => {
            card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            card.style.opacity = '1';
            card.style.transform = 'translateY(0)';
        }, 50 + i * 40);
    });
}

function _updateNameFields() {
    // Hide master name if Independent (intensity 0)
    const masterField = document.getElementById('field-master');
    if (masterField) {
        masterField.style.display = editorState.selectedIntensity === 0 ? 'none' : '';

        // Label must match the selected intensity, not always "Roommate".
        //   1 = Roommate, 2 = Landlord, 3 = Owner (renders as Master)
        const labelEl = masterField.querySelector('label');
        if (labelEl) {
            const keyByIntensity = {
                1: 'editor.roommate_name',
                2: 'editor.landlord_name',
                3: 'editor.master_name',
            };
            const fallback = {
                1: "Your Roommate's Name",
                2: "Your Landlord's Name",
                3: "Your Master's Name",
            };
            const key = keyByIntensity[editorState.selectedIntensity] || 'editor.roommate_name';
            const translated = L.get(key);
            labelEl.textContent = (translated && translated !== key) ? translated : (fallback[editorState.selectedIntensity] || "Your Roommate's Name");
            labelEl.setAttribute('data-l10n', key);
        }
    }
    // Partner name field: hidden on 'none', label/hint/placeholder swap
    // girlfriend <-> boyfriend (BOYFRIEND_START §3.1 — the input keeps feeding
    // GirlfriendName either way; the VAR simply holds his name on the BF route).
    const gfField = document.getElementById('field-girlfriend');
    if (gfField) {
        const opt = PARTNER_OPTIONS.find(p => p.id === editorState.partner);
        gfField.style.display = editorState.partner !== 'none' ? '' : 'none';
        if (opt && opt.nameLabelKey) {
            const labelEl = gfField.querySelector('label');
            if (labelEl) {
                const t = L.get(opt.nameLabelKey);
                labelEl.textContent = (t && t !== opt.nameLabelKey) ? t
                    : (editorState.partner === 'boyfriend' ? "Your Boyfriend's Name" : "Your Girlfriend's Name");
                labelEl.setAttribute('data-l10n', opt.nameLabelKey);
            }
            const hintEl = gfField.querySelector('.field-hint');
            if (hintEl && opt.nameHintKey) {
                const h = L.get(opt.nameHintKey);
                hintEl.textContent = (h && h !== opt.nameHintKey) ? h : `Leave blank to use "${opt.nameDefault}"`;
                hintEl.setAttribute('data-l10n', opt.nameHintKey);
            }
            const inputEl = gfField.querySelector('input');
            if (inputEl) inputEl.placeholder = opt.nameDefault || 'Sarah';
        }
    }
}

function buildPreview() {
    const name = sanitizeName(document.getElementById('input-name')?.value, 30) || 'Jamie';
    const bg = BACKGROUNDS.find(b => b.id === editorState.selectedBackground);
    const traits = editorState.selectedTraits.map(id => TRAITS.find(t => t.id === id));

    const previewName = document.getElementById('preview-name');
    const previewBg = document.getElementById('preview-bg');
    const previewTraits = document.getElementById('preview-traits');
    const previewStats = document.getElementById('preview-stats');

    if (previewName) previewName.textContent = name;
    if (previewBg && bg) {
        const bgIconHtml = bg.icon && bg.icon.includes('/') ? `<img src="${bg.icon}" alt="${bg.name}" width="20" height="20" style="vertical-align:middle;margin-right:4px">` : bg.icon;
        previewBg.innerHTML = `<strong>${bgIconHtml}${bg.name}</strong><br>${bg.bonus}`;
    }
    if (previewTraits) {
        previewTraits.innerHTML = traits
            .filter(Boolean)
            .map(t => {
                const tIconHtml = t.icon && t.icon.includes('/') ? `<img src="${t.icon}" alt="${t.name}" width="20" height="20" style="vertical-align:middle;margin-right:4px">` : t.icon;
                return `<strong>${tIconHtml}${t.name}</strong> -- ${t.bonus}`;
            })
            .join('<br>');
    }

    // Echo the 2-trait synergy flavor into the final review — the one piece of
    // copy that makes the build feel special, previously dropped at commit time.
    const previewSynergy = document.getElementById('preview-synergy');
    if (previewSynergy) {
        let synergyText = '';
        if (editorState.selectedTraits.length === 2) {
            const sorted = [...editorState.selectedTraits].sort();
            const key = `synergy.${sorted.join('_')}`;
            const local = L.get(key);
            synergyText = local !== key ? local : (SYNERGIES[sorted.join('+')] || '');
        }
        previewSynergy.textContent = synergyText;
        previewSynergy.style.display = synergyText ? '' : 'none';
    }
    if (previewStats && bg) {
        // Preview mirrors the actual Money set by the apply step below:
        // $200 base (baseVar.Money seed) + background bonus + active perks.
        // Every background starts on top of the $200 base, so a Money line is
        // always shown. (v0.2.7.3+ — restored the $200 base after the leak bug
        // where picking Extra Cash dropped the player from $200 to $50,
        // foundby:曉明.)
        //
        // Finding #1 fix (2026-05-27): preview also sums in active perks.
        // Pre-fix College Dropout + Fem Head Start showed "Femininity: 10"
        // while the actual in-game Fem was 15 (BG +10 + perk +5).
        // Perk bonuses mirror the apply step: perk_fem_head_start +5 Fem,
        // perk_sub_head_start +5 Sub, perk_extra_cash +$50, perk_energy_boost
        // +10 Max Energy.
        const BASE_MONEY = 200; // baseVar.Money seed in variables.js
        const perks = editorState.selectedPerks || [];
        const femBonus  = perks.includes('perk_fem_head_start') ? 5 : 0;
        const subBonus  = perks.includes('perk_sub_head_start') ? 5 : 0;
        const cashBonus = perks.includes('perk_extra_cash')     ? 50 : 0;
        const eBonus    = perks.includes('perk_energy_boost')   ? 10 : 0;
        const fem = (femAcc) => `Femininity: ${femAcc + femBonus}`;
        const sub = (subAcc) => `Submissiveness: ${subAcc + subBonus}`;
        const money = (bgMoney) => `Money: $${BASE_MONEY + bgMoney + cashBonus}`;
        const maxE = (eAcc) => `Max Energy: ${eAcc + eBonus}`;
        const statLines = [];
        if (bg.id === 1) statLines.push(`${money(100)}, ${maxE(105)}`);
        else if (bg.id === 2) statLines.push(`${money(0)}, ${fem(10)}, Shame: 5${subBonus ? `, ${sub(0)}` : ''}${eBonus ? `, ${maxE(100)}` : ''}`);
        else if (bg.id === 3) statLines.push(`${money(0)}, ${maxE(115)}, Fem resistance active${femBonus ? `, ${fem(0)}` : ''}${subBonus ? `, ${sub(0)}` : ''}`);
        else if (bg.id === 4) statLines.push(`${money(0)}, ${fem(5)}, Followers: 10${subBonus ? `, ${sub(0)}` : ''}${eBonus ? `, ${maxE(100)}` : ''}`);
        else if (bg.id === 5) statLines.push(`${money(0)}, ${sub(10)}, Arousal: 5${femBonus ? `, ${fem(0)}` : ''}${eBonus ? `, ${maxE(100)}` : ''}`);
        else if (bg.id === 6) statLines.push(`${money(150)}, Sub: ${15 + subBonus}, Shame: 10${femBonus ? `, ${fem(0)}` : ''}${eBonus ? `, ${maxE(100)}` : ''}`);
        else if (bg.id === 7) statLines.push(`${money(0)}, ${maxE(120)}, Fem resistance active${femBonus ? `, ${fem(0)}` : ''}${subBonus ? `, ${sub(0)}` : ''}`);
        previewStats.innerHTML = statLines.join('<br>');
    }

    // Show intensity + partner in preview
    const previewIntensity = document.getElementById('preview-intensity');
    if (previewIntensity) {
        const int = INTENSITIES.find(i => i.id === editorState.selectedIntensity);
        let intText = int ? `<strong>${int.name}</strong>` : 'Landlord';
        if (editorState.partner !== 'girlfriend') {
            const pOpt = PARTNER_OPTIONS.find(p => p.id === editorState.partner);
            const pName = pOpt ? _l10nOr(`${pOpt.l10nKey}.name`, pOpt.name) : '';
            if (pName) intText += `<br><em>${pName}</em>`;
        }
        previewIntensity.innerHTML = intText;
    }

    // Show selected perks in preview
    const previewPerks = document.getElementById('preview-perks');
    if (previewPerks) {
        if (editorState.selectedPerks.length > 0) {
            previewPerks.innerHTML = editorState.selectedPerks
                .map(id => { const p = PERKS.find(x => x.id === id); return p ? `${p.icon} ${L.get(p.nameKey)}` : id; })
                .join(', ');
        } else {
            const _none = L.get('ui.none_selected');
            previewPerks.innerHTML = '<em>' + (_none !== 'ui.none_selected' ? _none : 'None selected') + '</em>';
        }
    }
}

// Perk-step tabs (0.2.9): "Perks" (positive, point-gated) / "Handicaps"
// (negative, free). Sets the active class + wires clicks once.
function _renderPerkTabs() {
    const tabBar = document.getElementById('perk-tabs');
    if (!tabBar) return;
    const active = editorState.perkTab || 'perks';
    tabBar.querySelectorAll('.perk-tab').forEach(btn => {
        const on = (btn.dataset.tab === active);
        btn.classList.toggle('active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
        if (!btn.dataset.wired) {
            btn.dataset.wired = '1';
            btn.addEventListener('click', () => {
                editorState.perkTab = btn.dataset.tab;
                buildPerkGrid();
            });
        }
    });
}

function buildPerkGrid() {
    const grid = document.getElementById('perk-grid');
    if (!grid) return;
    grid.innerHTML = '';

    // Calculate available perk points (unlocked gameplay achievements)
    editorState.perkPoints = achievementManager.getGameplayPoints?.() || 0;
    const pointsEl = document.getElementById('perk-points');
    const available = editorState.perkPoints - _perkSpent();
    // {total} shows the stable budget so the live-updating available/spent
    // numbers can't be mistaken for it (Discord report 2026-06-03: player
    // read "13 points available (17 spent)" mid-selection as "13 budget,
    // 17 spent, math broken"). Locale strings without {total} degrade fine.
    if (pointsEl) pointsEl.textContent = L.get('perk.points_available')
        .replace('{available}', available)
        .replace('{total}', editorState.perkPoints)
        .replace('{spent}', _perkSpent());

    // Two-tab split (0.2.9): "Perks" tab = the point-gated positive categories,
    // "Handicaps" tab = the free negative perks (Hardship category, cost 0). The
    // no-points notice only belongs on the Perks tab; Handicaps are always free,
    // which is what makes them reachable on a fresh / Free save (0 points).
    _renderPerkTabs();
    const tab = editorState.perkTab || 'perks';
    const POSITIVE_CATS = ['Stat', 'Gameplay', 'Social', 'Streaming'];
    const tabCats = (tab === 'handicaps') ? ['Hardship'] : POSITIVE_CATS;

    const noPoints = editorState.perkPoints === 0;
    if (noPoints && tab === 'perks') {
        const notice = document.createElement('div');
        notice.className = 'perk-no-points';
        notice.innerHTML = L.get('perk.no_points') + '<br><span style="color:var(--text-muted);font-size:12px">' + L.get('perk.no_points_hint') + '</span>';
        grid.appendChild(notice);
    }

    for (const cat of tabCats) {
        const catPerks = PERKS.filter(p => p.category === cat);
        if (catPerks.length === 0) continue;

        // Skip the redundant category header when the tab holds a single
        // category (Handicaps tab is all one category and the tab labels it).
        if (tabCats.length > 1) {
            const header = document.createElement('div');
            header.className = 'perk-category-header';
            header.textContent = L.get(PERK_CATEGORY_KEYS[cat] || cat);
            grid.appendChild(header);
        }

        for (const perk of catPerks) {
            const selected = editorState.selectedPerks.includes(perk.id);
            const spent = _perkSpent();
            const canAfford = (editorState.perkPoints - spent) >= perk.cost;
            const pName = L.get(perk.nameKey);
            const pDesc = L.get(perk.descKey);
            const costLabel = L.get('perk.cost');

            // Pacing-Note (only for perks that affect pacing, e.g. perk_fem_head_start)
            let perkPacingNoteText = perk.pacingNote;
            if (perk.pacingNoteKey) {
                const localNote = L.get(perk.pacingNoteKey);
                if (localNote !== perk.pacingNoteKey) perkPacingNoteText = localNote;
            }
            const pacingNoteHtml = perk.pacingNote
                ? `<div class="card-pacing-note pacing-${perk.pacing}">\u23f5 ${perkPacingNoteText}</div>`
                : '';

            const card = document.createElement('div');
            card.className = 'card perk-card' + (selected ? ' selected' : '')
                + (perk.isNegative ? ' perk-hardship' : '')
                + (!canAfford && !selected ? ' dimmed' : '');
            card.innerHTML = `
                <span class="card-check">\u2713</span>
                <div class="card-icon">${perk.icon}</div>
                <div class="card-name">${pName}</div>
                <div class="card-desc">${pDesc}</div>
                ${pacingNoteHtml}
                ${perk.isNegative ? `<div class="card-bonus card-bonus-hardship">${L.get('perk.hardship_badge')}</div>` : `<div class="card-bonus">${costLabel}: ${perk.cost}</div>`}
            `;
            card.addEventListener('click', () => _togglePerk(perk.id));
            _wireCardA11y(card, 'checkbox');
            grid.appendChild(card);
        }
    }
    _refreshAriaChecked();
}

function _togglePerk(perkId) {
    const idx = editorState.selectedPerks.indexOf(perkId);
    if (idx >= 0) {
        editorState.selectedPerks.splice(idx, 1);
    } else {
        const perk = PERKS.find(p => p.id === perkId);
        if (!perk) return;
        if (_perkSpent() + perk.cost > editorState.perkPoints) return;
        editorState.selectedPerks.push(perkId);
    }
    buildPerkGrid();
}

function _perkSpent() {
    return editorState.selectedPerks.reduce((sum, id) => {
        const p = PERKS.find(x => x.id === id);
        return sum + (p ? p.cost : 0);
    }, 0);
}

// ═══════════════════════════════════════════════════════════════════════════
// Archetype quick-start entry screens (0.3.1 editor rework)
//
// Two screens rendered BEFORE the 6-phase wizard: persona picker + confirm.
// They live OUTSIDE the .phase list / data-phase counter — editorNextPhase's
// pre/post-increment build-hook conventions must never see them. Presets are
// PURE editorState pre-fills: Begin runs the untouched startNewGame()
// pipeline, so Ink writes are byte-identical to a hand-clicked wizard run
// (locked by tests/regression/archetype-quickstart.spec.js).
// ═══════════════════════════════════════════════════════════════════════════

// Compose recap chips from EXISTING card strings (zero new L10n cost beyond
// persona name + tagline). `s` is a picks-shaped object; on the confirm screen
// it is ALWAYS re-derived from editorState, never the preset literal.
function _archetypeChips(s, { includePartner = true } = {}) {
    const chips = [];
    const bg = BACKGROUNDS.find(b => b.id === s.background);
    if (bg) chips.push({ jump: 0, text: _l10nOr(`bg.${bg.l10nKey || bg.id}.name`, bg.name) });
    (s.traits || []).forEach(tid => {
        const t = TRAITS.find(x => x.id === tid);
        if (t) chips.push({ jump: 1, text: _l10nOr(`trait.${t.id}.name`, t.name) });
    });
    const int = INTENSITIES.find(i => i.id === s.intensity);
    if (int) chips.push({ jump: 2, text: _l10nOr(`intensity.${int.key}.name`, int.name) });
    if ((s.mascPath ?? 0) >= 1) {
        chips.push({ jump: 2, text: _l10nOr('editor.masc_masculine.name', 'Stay your own man'), hot: true });
    }
    const days = s.pacingDays ?? 60;
    const z = _pacingZone(days);
    chips.push({ jump: 5, text: `${_l10nOr(z.nameKey, z.name)} · ${days} ${_l10nOr('editor.pacing_days', 'days')}`, hot: true });
    if (includePartner) {
        const pOpt = PARTNER_OPTIONS.find(p => p.id === (s.partner || 'girlfriend'));
        if (pOpt) chips.push({ jump: null, text: _l10nOr(`${pOpt.l10nKey}.name`, pOpt.name) });
    }
    const diff = _difficultyLabelFor(_difficultyForPicks(s.background, s.traits, s.intensity));
    chips.push({ jump: null, text: diff.label, cls: 'archetype-chip-' + diff.cls });
    return chips;
}

// One-shot nav cards (persona / Build Your Own) — role=button, not radio.
function _wireEntryCardA11y(card) {
    card.setAttribute('tabindex', '0');
    card.setAttribute('role', 'button');
    card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            card.click();
        }
    });
}

function applyArchetype(a) {
    // Kill-switch leak guard: no preset ships partner:'boyfriend' while the
    // v031 switch is dormant, but coerce defensively anyway.
    const partner = (a.picks.partner === 'boyfriend' && !_v031On()) ? 'girlfriend' : (a.picks.partner || 'girlfriend');
    editorState = {
        ..._defaultEditorState(),
        archetypeId: a.id,
        selectedBackground: a.picks.background,
        selectedTraits: [...a.picks.traits],
        selectedIntensity: a.picks.intensity,
        mascPath: a.picks.mascPath ?? 0,
        orientationLean: a.picks.orientationLean ?? 0,
        partner,
        pacingTargetDays: a.picks.pacingDays ?? 60,
    };
    // Masc <-> intensity coupling, defense-in-depth (presets bundle 0 already).
    if (editorState.mascPath >= 1) editorState.selectedIntensity = 0;
    // Keep the wizard's pacing slider in sync so "customize" shows the preset
    // value instead of the stale DOM default.
    const slider = document.getElementById('pacing-slider');
    if (slider) {
        slider.value = String(editorState.pacingTargetDays);
        _updatePacingSliderReadout(slider.value);
    }
}

function renderArchetypes() {
    const grid = document.getElementById('archetype-grid');
    if (!grid) return;
    grid.innerHTML = '';

    // "Last build" replay chip — fastest re-roll for returning players.
    const lastWrap = document.getElementById('archetype-last-build');
    if (lastWrap) {
        lastWrap.innerHTML = '';
        let stored = null;
        try { stored = JSON.parse(localStorage.getItem('PnO_CharacterData') || 'null'); } catch (e) { /* corrupt/absent */ }
        const lastA = stored && stored.archetypeId ? ARCHETYPES.find(x => x.id === stored.archetypeId) : null;
        if (lastA) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'archetype-last-build-chip';
            btn.textContent = `${_l10nOr('editor.archetype_last_build', 'Play it again:')} ${_l10nOr(lastA.nameKey, lastA.name)}`;
            btn.addEventListener('click', () => {
                applyArchetype(lastA);
                if (stored.partner && PARTNER_OPTIONS.some(p => p.id === stored.partner && (!p.gated || _v031On()))) {
                    editorState.partner = stored.partner;
                }
                _showConfirm();
                const nameInput = document.getElementById('input-archetype-name');
                if (nameInput && stored.name) nameInput.value = stored.name;
                const pInput = document.getElementById('input-archetype-partner-name');
                if (pInput && stored.girlfriendName) pInput.value = stored.girlfriendName;
            });
            lastWrap.appendChild(btn);
        }
    }

    const recommendedLabel = L.get('editor.recommended');
    const recText = recommendedLabel !== 'editor.recommended' ? recommendedLabel : 'Recommended';

    ARCHETYPES.forEach(a => {
        const card = document.createElement('div');
        card.className = 'card card-with-thumb archetype-card';
        card.dataset.archetypeId = a.id;
        const chips = _archetypeChips(a.picks, { includePartner: true });
        const chipHtml = chips.map(c =>
            `<span class="archetype-chip${c.cls ? ' ' + c.cls : ''}${c.hot ? ' archetype-chip-hot' : ''}">${c.text}</span>`
        ).join('');
        const recommendedHtml = a.recommended ? `<span class="card-recommended">${recText}</span>` : '';
        const warnHtml = a.warnKey ? `<span class="archetype-warn">${_l10nOr(a.warnKey, a.warn || '')}</span>` : '';
        card.innerHTML = `
            ${_thumbHtml(a.thumb)}
            ${recommendedHtml}
            ${warnHtml}
            <div class="card-name">${_l10nOr(a.nameKey, a.name)}</div>
            <div class="card-desc archetype-tagline">${_l10nOr(a.taglineKey, a.tagline)}</div>
            <div class="archetype-chips">${chipHtml}</div>
        `;
        card.addEventListener('click', () => {
            applyArchetype(a);
            _showConfirm();
        });
        _wireEntryCardA11y(card);
        grid.appendChild(card);
    });

    // Equal-weight "Build Your Own" card into the untouched full wizard.
    const custom = document.createElement('div');
    custom.className = 'card archetype-card archetype-card-custom';
    custom.dataset.archetypeId = 'custom';
    custom.innerHTML = `
        <div class="card-icon">🛠️</div>
        <div class="card-name">${_l10nOr('editor.archetype_custom.name', 'Build Your Own')}</div>
        <div class="card-desc">${_l10nOr('editor.archetype_custom.desc', 'The full editor. Backgrounds, traits, perks, pacing. Every option.')}</div>
    `;
    custom.addEventListener('click', () => {
        // Fresh defaults — a previously PREVIEWED preset (card click -> Back)
        // must not leak into a hand-built run (review finding 2026-07-15).
        // The carry-picks path is the confirm screen's "customize" button.
        editorState = _defaultEditorState();
        enterWizard(0);
    });
    _wireEntryCardA11y(custom);
    grid.appendChild(custom);
}

// Confirm-screen partner name field (mirrors _updateNameFields' swap logic).
function _updateConfirmPartnerNameField() {
    const field = document.getElementById('field-archetype-partner-name');
    if (!field) return;
    const opt = PARTNER_OPTIONS.find(p => p.id === editorState.partner);
    field.style.display = editorState.partner !== 'none' ? '' : 'none';
    if (opt && opt.nameLabelKey) {
        const labelEl = field.querySelector('label');
        if (labelEl) {
            const t = L.get(opt.nameLabelKey);
            labelEl.textContent = (t && t !== opt.nameLabelKey) ? t
                : (editorState.partner === 'boyfriend' ? "Your Boyfriend's Name" : "Your Girlfriend's Name");
            labelEl.setAttribute('data-l10n', opt.nameLabelKey);
        }
        const inputEl = field.querySelector('input');
        if (inputEl) inputEl.placeholder = opt.nameDefault || 'Sarah';
    }
}

// Copy non-blank confirm inputs into the canonical wizard inputs, which are
// the single source startNewGame reads. Blank keeps the Jamie/Sarah/Jesse
// defaults working exactly like the wizard path.
function _copyConfirmNames() {
    const n = document.getElementById('input-archetype-name');
    const main = document.getElementById('input-name');
    if (n && main && n.value.trim()) main.value = n.value;
    const p = document.getElementById('input-archetype-partner-name');
    const gf = document.getElementById('input-gf');
    if (p && gf && p.value.trim()) gf.value = p.value;
}

function renderConfirm() {
    const a = ARCHETYPES.find(x => x.id === editorState.archetypeId);
    const nameEl = document.getElementById('archetype-confirm-name');
    const tagEl = document.getElementById('archetype-confirm-tagline');
    if (nameEl) nameEl.textContent = a ? _l10nOr(a.nameKey, a.name) : _l10nOr('editor.your_character', 'Your Character');
    if (tagEl) tagEl.textContent = a ? _l10nOr(a.taglineKey, a.tagline) : '';

    // Recap chips — re-derived from editorState (NEVER the preset literal, so
    // customize-then-Back can't show stale picks). Tappable chips deep-link
    // into the matching wizard phase, pre-filled.
    const chipsEl = document.getElementById('archetype-confirm-chips');
    if (chipsEl) {
        chipsEl.innerHTML = '';
        const chips = _archetypeChips({
            background: editorState.selectedBackground,
            traits: editorState.selectedTraits,
            intensity: editorState.selectedIntensity,
            mascPath: editorState.mascPath,
            pacingDays: editorState.pacingTargetDays,
        }, { includePartner: false });
        chips.forEach(c => {
            const el = document.createElement(c.jump == null ? 'span' : 'button');
            if (c.jump != null) {
                el.type = 'button';
                el.addEventListener('click', () => {
                    _copyConfirmNames();
                    enterWizard(c.jump);
                });
            }
            el.className = 'archetype-chip'
                + (c.cls ? ' ' + c.cls : '')
                + (c.hot ? ' archetype-chip-hot' : '')
                + (c.jump != null ? ' archetype-chip-link' : '');
            el.textContent = c.text;
            chipsEl.appendChild(el);
        });
    }

    // Partner selector chips (girlfriend / boyfriend[gated] / none).
    const row = document.getElementById('archetype-partner-row');
    if (row) {
        row.innerHTML = '';
        PARTNER_OPTIONS.filter(o => !o.gated || _v031On()).forEach(o => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'partner-chip' + (editorState.partner === o.id ? ' selected' : '');
            b.dataset.partnerId = o.id;
            b.setAttribute('role', 'radio');
            b.setAttribute('aria-checked', editorState.partner === o.id ? 'true' : 'false');
            b.textContent = _l10nOr(`${o.l10nKey}.name`, o.name);
            b.addEventListener('click', () => {
                editorState.partner = o.id;
                row.querySelectorAll('.partner-chip').forEach(x => {
                    const on = x.dataset.partnerId === o.id;
                    x.classList.toggle('selected', on);
                    x.setAttribute('aria-checked', on ? 'true' : 'false');
                });
                _updateConfirmPartnerNameField();
                _updatePartnerOrientationNote();
            });
            row.appendChild(b);
        });
    }

    // Fresh inputs per confirm entry (defaults hint covers blank).
    const nameInput = document.getElementById('input-archetype-name');
    if (nameInput) nameInput.value = '';
    const pInput = document.getElementById('input-archetype-partner-name');
    if (pInput) pInput.value = '';
    _updateConfirmPartnerNameField();
    _updatePartnerOrientationNote();

    // Content prefs on the preset path too — same visibility gate as the
    // wizard section (Patreon opt-ins reachable without the full editor).
    const cpWrap = document.getElementById('archetype-content-prefs');
    if (cpWrap) {
        const show = CONTENT_PREFS_ENABLED && hasVisibleContentTracks(!isPat());
        cpWrap.style.display = show ? '' : 'none';
        if (show) {
            buildContentPrefCards(editorState.contentPrefs, '#archetype-content-prefs-grid');
            try { tutorialTips.markSeen('content_prefs'); } catch (e) { /* non-fatal */ }
        }
    }

    // Perk-points pill — surfaces banked achievement points that used to hide
    // in a dead wizard phase; deep-links into the perk phase pre-filled.
    const pill = document.getElementById('archetype-perk-pill');
    if (pill) {
        const pts = achievementManager.getGameplayPoints?.() || 0;
        if (pts > 0) {
            pill.style.display = '';
            pill.textContent = _l10nOr('editor.archetype_perk_pill', 'You have {n} perk points. Spend them?')
                .replace('{n}', String(pts));
        } else {
            pill.style.display = 'none';
        }
    }
}

// ── Entry-screen visibility management ─────────────────────────────────────
function _setEntryMode(on) {
    const screen = document.getElementById('screen-character-editor');
    if (screen) screen.classList.toggle('editor-mode-entry', on);
}

function showEntry() {
    _setEntryMode(true);
    renderArchetypes();
    const arch = document.getElementById('phase-archetype');
    const conf = document.getElementById('phase-archetype-confirm');
    if (arch) arch.classList.add('active');
    if (conf) conf.classList.remove('active');
    _onPhaseShown(arch);
    _staggerCards(arch);
}

function _showConfirm() {
    renderConfirm();
    const arch = document.getElementById('phase-archetype');
    const conf = document.getElementById('phase-archetype-confirm');
    if (arch) arch.classList.remove('active');
    if (conf) conf.classList.add('active');
    _onPhaseShown(conf);
}

// Direct wizard-phase jump for entry-screen deep links. Mirrors the build-hook
// conventions in editorNextPhase (perk grid before 4 shows, preview before 5,
// name fields once 3 is current).
function _gotoPhase(n) {
    if (n === 4) buildPerkGrid();
    if (n === 5) buildPreview();
    const phases = document.querySelectorAll('.phase');
    const target = Math.max(0, Math.min(n, phases.length - 1));
    phases.forEach((p, i) => {
        p.classList.toggle('active', i === target);
        p.classList.remove('phase-exit-left', 'phase-exit-right', 'phase-enter-left', 'phase-enter-right');
    });
    editorState.phase = target;
    if (target === 3) _updateNameFields();
    _updateDots(document.querySelectorAll('.progress-dots .dot'));
    _onPhaseShown(phases[target]);
    _staggerCards(phases[target]);
}

function enterWizard(phaseIndex = 0) {
    _setEntryMode(false);
    const arch = document.getElementById('phase-archetype');
    const conf = document.getElementById('phase-archetype-confirm');
    if (arch) arch.classList.remove('active');
    if (conf) conf.classList.remove('active');
    // Full repaint from editorState so a preset opens the wizard as if
    // hand-picked (background/traits/partner/coupling/slider included).
    characterEditorUI.applyDefaultSelections();
    _gotoPhase(phaseIndex);
}

// Wired from bind(). The Begin button carries data-action="start-game", so the
// generic bind() loop attaches startNewGame; THIS listener must be registered
// FIRST (same element, registration order) so the confirm-input names land in
// the canonical inputs before startNewGame reads them.
function _bindArchetypeUi() {
    const begin = document.getElementById('btn-archetype-begin');
    if (begin) begin.addEventListener('click', () => _copyConfirmNames());
    const back = document.getElementById('btn-archetype-back');
    if (back) back.addEventListener('click', () => showEntry());
    const customize = document.getElementById('btn-archetype-customize');
    if (customize) customize.addEventListener('click', () => {
        _copyConfirmNames();
        enterWizard(0);
    });
    const pill = document.getElementById('archetype-perk-pill');
    if (pill) pill.addEventListener('click', () => {
        _copyConfirmNames();
        enterWizard(4);
    });
    ['input-archetype-name', 'input-archetype-partner-name'].forEach(id => {
        const input = document.getElementById(id);
        if (input) {
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('btn-archetype-begin')?.click();
                }
            });
        }
    });
}

// True while editorState still equals its seeding preset's picks (perks/
// hardships count as divergence; partner + pacing tweaks on the confirm
// screen do not — they're offered there as intentional variation points).
function _picksMatchArchetype() {
    const a = ARCHETYPES.find(x => x.id === editorState.archetypeId);
    if (!a) return false;
    const p = a.picks;
    const sameTraits = editorState.selectedTraits.length === p.traits.length
        && p.traits.every(t => editorState.selectedTraits.includes(t));
    return editorState.selectedBackground === p.background
        && sameTraits
        && editorState.selectedIntensity === p.intensity
        && (editorState.mascPath ?? 0) === (p.mascPath ?? 0)
        && (editorState.orientationLean ?? 0) === (p.orientationLean ?? 0)
        && (editorState.selectedPerks || []).length === 0;
}

let _starting = false;

async function startNewGame() {
    // Re-entrancy guard: startNewGame is async (network fetch + item grants +
    // story init against shared singletons). A double-tap on "Begin Your Story"
    // would run two concurrent inits (double grants / race). Block it and turn
    // the button into a busy affordance — doubles as the editor→game transition.
    if (_starting) return;
    _starting = true;
    const startBtns = [...document.querySelectorAll('[data-action="start-game"]')];
    startBtns.forEach(b => {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
        b.classList.add('is-starting');
    });

    // Pacing 2.0: the story-length knob (35-90, default 60 = Normal). Everything
    // scales off this; pacing_mode is derived downstream in the Ink-apply block.
    // editorState is the source of truth since 0.3.1 (slider input syncs it,
    // archetype presets set it); the DOM read stays as a fallback for stale
    // callers that never touched the field.
    let pacingTargetDays = (typeof editorState.pacingTargetDays === 'number')
        ? editorState.pacingTargetDays : 60;
    try {
        if (typeof editorState.pacingTargetDays !== 'number') {
            const slider = document.getElementById('pacing-slider');
            if (slider) pacingTargetDays = parseInt(slider.value, 10) || 60;
        }
    } catch (e) { /* slider missing, fallback to default */ }

    // Skip-Setup-Phase checkbox: replays / experienced players. Starts Day 8
    // with established routine + a recap message of what got skipped.
    let skipSetup = false;
    try {
        skipSetup = !!document.getElementById('skip-setup-checkbox')?.checked;
    } catch (e) { /* checkbox missing, fallback to default */ }

    // Sanitize names at input boundary — strip control chars + HTML-unsafe
    // chars + cap length 30. Save files are shared between players (bug-report
    // flow, translation editor, Discord), so a poisoned MasterName from one
    // player's export can XSS another's session at render time. Belt-and-
    // braces with escapeHtml() at render sites. (Audit 2026-05-23.)
    const data = {
        name: sanitizeName(document.getElementById('input-name')?.value, 30) || 'Jamie',
        // 2nd name (player request 0.2.8.2). Defaults to "Mia" if blank (owner:
        // "der fem name sollte auch ein default haben"), so every PC gets the
        // feminize-name switch at Fem>=50. (Pre-feature saves keep FeminineName=""
        // -> display_name() falls back to the masculine name, no surprise switch.)
        feminineName: sanitizeName(document.getElementById('input-fem-name')?.value, 30) || 'Mia',
        masterName: sanitizeName(document.getElementById('input-master')?.value, 30) || 'Alex',
        // GirlfriendName holds the partner's name on BOTH partner routes
        // (BOYFRIEND_START §1 legacy-compat) — only the blank-default differs.
        girlfriendName: sanitizeName(document.getElementById('input-gf')?.value, 30)
            || (editorState.partner === 'boyfriend' ? 'Jesse' : 'Sarah'),
        background: editorState.selectedBackground,
        traits: editorState.selectedTraits,
        perks: editorState.selectedPerks || [],
        masterIntensity: editorState.selectedIntensity,
        partner: editorState.partner || 'girlfriend',
        girlfriendDisabled: editorState.partner === 'none',
        partnerIsMale: editorState.partner === 'boyfriend',
        // Only label the run with the preset id if the picks still MATCH the
        // preset — a customized run must not feed a misleading "Play it again:
        // <persona>" chip next session (review finding 2026-07-15). Partner is
        // deliberately excluded: the confirm screen offers it as a variation.
        archetypeId: _picksMatchArchetype() ? editorState.archetypeId : null,
        mascPath: editorState.mascPath ?? 0,
        orientationLean: editorState.orientationLean ?? 0,
        pacingTargetDays: pacingTargetDays,
        skipSetup: skipSetup,
    };

    try {
        localStorage.setItem('PnO_CharacterData', JSON.stringify(data));
    } catch (e) {
        console.warn('[PnO] Failed to save character data:', e);
    }

    if (_opts.setCharacterData) _opts.setCharacterData(data);

    // ── Full reset of all game state (critical for New Game after Quit) ──
    // Clear old auto-save so it doesn't interfere
    try { await saveSystem.deleteAutoSave(); } catch(e) {}

    // Reset phone SMS threads + clear app install flags from previous game
    phoneUI.deserialize(null);
    for (const app of ['grindr','onlyfems','sissysocial','jobfinder','dareme','cheats','hypno']) {
        try { localStorage.removeItem('PnO_App_' + app); } catch(e) {}
    }

    // Reset statistics (per-playthrough); keep achievements (perk points persist across games)
    if (statisticsTracker?.resetAll) statisticsTracker.resetAll();

    // Clear story content display
    const storyContentEl = document.getElementById('story-content');
    if (storyContentEl) storyContentEl.innerHTML = '';

    // Clear choice tooltips
    document.querySelectorAll('.choice-hover-tip').forEach(t => t.remove());

    // Load and start Ink story
    try {
        const storyJson = await fetchStoryJson();

        // Reset variable manager for new game
        variableManager.resetForNewGame();

        // Set character names
        variableManager.setCharacterNames(data.name, data.masterName, data.girlfriendName, data.feminineName);

        // Bar manager push lives at line ~599 below (story.variablesState write
        // AFTER initStory completes). Pre-fix this had a setValue('BarManagerName')
        // here which landed in baseVar pre-init and stuck across save/load —
        // the direct-Ink write below replaces it cleanly. (Audit 2026-05-23.)

        // Give starter items (tshirt, jeans, boxershorts, sneakers).
        // Awaited so the optional item-DB recovery fetch (when boot-time
        // fetch failed and left _itemDb empty) completes before any
        // subsequent equipment writes.
        await inventoryManager.giveStarterItems();

        // Register Ink pusher so equipment buffs + flags affect gameplay
        inventoryManager.registerInkPusher((name, value) => {
            if (typeof value === 'boolean') {
                // Boolean flags (is_dressed, wearing_chastity, EquippedFeminine, etc.)
                variableManager.setFlag(name, value);
            } else {
                // Numeric buff values (Allure, Comfort, Femininity, etc.)
                variableManager.setBuffedVar(name, value);
            }
        });
        inventoryManager.registerInkReader((name) => {
            const story = storyManager.currentStory;
            if (!story) return 0;
            try { return story.variablesState[name]; } catch(e) { return 0; }
        });

        // Wire up choice handler
        choicesUI.setChoiceHandler((index) => storyManager.selectChoice(index));

        // Initialize story WITHOUT starting it yet (no refreshView)
        storyManager.initStory(storyJson);

        // Set background + traits in Ink
        try {
            const story = storyManager.currentStory;
            if (story) {
                const bgId = data.background || 1;
                story.variablesState['player_background'] = bgId;
                story.variablesState['trait_1'] = data.traits?.[0] || '';
                story.variablesState['trait_2'] = data.traits?.[1] || '';

                // Master intensity (0=independent, 1=roommate, 2=landlord, 3=owner).
                // MASC_ROUTE (0.2.8): a masc-path PC starts Independent — the
                // override must land BEFORE the dependent computations below
                // (difficulty_score, BarManagerName). Pre-fix it ran after them:
                // difficulty_score kept the discarded Roommate/Owner diff (+1..+4
                // toward Hard/Challenge-Mode achievements) and the bar manager
                // was named after a Master that doesn't exist in the run.
                // 0.2.9 (foundby:Maj): this override is now MIRRORED in the editor
                // UI by _syncMascIntensityCoupling — picking "Stay your own man"
                // visibly snaps the intensity to Independent + greys the Master
                // cards + shows a note, so this is no longer a silent discard.
                // Kept as defense-in-depth (authoritative write).
                const intensity = ((data.mascPath ?? 0) >= 1) ? 0 : (data.masterIntensity ?? 2);
                story.variablesState['master_intensity'] = intensity;
                console.log(`[PnO] Master intensity: ${intensity}`);

                // difficulty_score (foundby:Krama 0.2.8): sum of background +
                // trait + intensity `diff` modifiers — same formula the editor
                // already uses for its difficulty LABEL (updateDifficulty). Was
                // NEVER pushed before (Globals comment said "set from C#", a
                // Unity leftover) → it stayed 0 forever → the Hard Way /
                // Unbreakable ending achievements (gated on difficulty_score)
                // were UNREACHABLE in the entire web build. Ink-only var, not in
                // OBSERVED_VARIABLES, so direct-write here like master_intensity.
                try {
                    let diffScore = 0;
                    const bgDef = BACKGROUNDS.find(b => b.id === bgId);
                    if (bgDef) diffScore += (bgDef.diff || 0);
                    for (const tId of (data.traits || [])) {
                        const tDef = TRAITS.find(x => x.id === tId);
                        if (tDef) diffScore += (tDef.diff || 0);
                    }
                    const intDef = INTENSITIES.find(i => i.id === intensity);
                    if (intDef) diffScore += (intDef.diff || 0);
                    // Hardship perks (0.2.9): each selected hardship adds its
                    // diffWeight so a handicap build reads as Hard/Challenge and
                    // counts toward the Hard Way / Unbreakable ending achievements.
                    for (const pId of (data.perks || [])) {
                        const pDef = PERKS.find(x => x.id === pId);
                        if (pDef && pDef.diffWeight) diffScore += pDef.diffWeight;
                    }
                    story.variablesState['difficulty_score'] = diffScore;
                    console.log(`[PnO] difficulty_score: ${diffScore}`);
                } catch (e) { console.warn('[Editor] difficulty_score push failed:', e?.message); }

                // Bar manager — Master IS the bar owner on Roommate/Landlord/Owner
                // (intensity >= 1). On Independent (0) the bar belongs to a generic
                // 'Marcus'. The earlier setValue() call lands in baseVar only because
                // _story is null pre-initStory, and BarManagerName is not in
                // OBSERVED_VARIABLES so pushVariablesToInk skips it. Write directly
                // to Ink here so {BarManagerName} interpolates correctly.
                try {
                    const barMgr = intensity >= 1 ? data.masterName : 'Marcus';
                    story.variablesState['BarManagerName'] = barMgr;
                } catch (e) { console.warn('[Editor] BarManagerName push failed:', e?.message); }

                // Partner choice (BOYFRIEND_START §3.1 truth table — polarity:
                // girlfriend_disabled=false means partner ACTIVE):
                //   girlfriend → disabled=false, partner_is_male=false
                //   boyfriend  → disabled=false, partner_is_male=true
                //   none       → disabled=true + broken_up=true, partner_is_male=false
                if (data.girlfriendDisabled) {
                    story.variablesState['girlfriend_disabled'] = true;
                    story.variablesState['girlfriend_broken_up'] = true;
                    console.log('[PnO] Partner storyline disabled');
                }
                // Route VAR, Ink-only, post-initStory direct write (BUG_PATTERNS
                // §44). Written unconditionally like masc_path — default false is
                // the inert legacy path while Boyfriend.ink is in-flight.
                try {
                    story.variablesState['partner_is_male'] = !!data.partnerIsMale;
                    if (data.partnerIsMale) console.log('[PnO] Partner: boyfriend route');
                } catch (e) { console.warn('[Editor] partner_is_male push failed:', e?.message); }

                // Content Preferences (0.3, CONTENT_TOGGLES): write each opted-in
                // track's pref VAR. Default [] = all off (byte-identical when dormant).
                // Skip placeholder tracks — they have no pref_<id> Ink VAR, so the
                // write would throw "Cannot assign to undeclared variable" (caught
                // below, but that masks real errors on every new game). Mirrors the
                // render/bind loops in content-tracks.js which already skip them.
                for (const t of CONTENT_TRACKS) {
                    if (t.placeholder) continue;
                    try { story.variablesState[`pref_${t.id}`] = editorState.contentPrefs.includes(t.id); }
                    catch (e) { /* pref VAR not declared for this track yet */ }
                }

                // MASC_ROUTE (0.2.8): write the route markers. Default 0/0 = the
                // legacy feminize path (byte-identical to pre-route saves) when the
                // cards are dormant/hidden. Written directly to Ink here (post-init,
                // pre-pushVariablesToInk) like master_intensity — they are Ink-only
                // route VARs, not in OBSERVED_VARIABLES.
                try {
                    story.variablesState['masc_path'] = data.mascPath ?? 0;
                    story.variablesState['orientation_lean'] = data.orientationLean ?? 0;
                    // MASC_ROUTE: a masc-path PC starts Independent (no feminizing
                    // Owner) — the master_intensity=0 override is applied at the
                    // `intensity` computation above, BEFORE difficulty_score and
                    // BarManagerName derive from it. Reclaim PCs who already carry
                    // an Owner are handled by the per-site masc_path==0 gates.
                } catch (e) { console.warn('[Editor] masc route push failed:', e?.message); }

                // Apply background stat bonuses (matches GameViewController.ApplyBackground).
                // max_energy is an Ink-COMPUTED var (get_max_energy() = 100 +
                // max_energy_base_bonus + bed/decor/mastery/wellness bonuses).
                // Write the base bonus to max_energy_base_bonus (Ink-only) and
                // set max_energy DIRECTLY on story.variablesState so the bar
                // shows the bonus from Day 1. Do NOT use setValue() — that seeds
                // baseVar.max_energy, which goes stale the moment a bed/decor
                // upgrade recomputes max_energy and then clobbers the correct
                // Ink value on reload. pushVariablesToInk now skips max_energy
                // (see variables.js), so the direct Ink write survives the boot
                // push. Same applies to perk_energy_boost below.
                // Starting money: every background stacks ON TOP of the $200
                // base (baseVar.Money seed in variables.js _createStartVariables).
                // Seed the Ink value from that base FIRST so the additive
                // bg/perk/skip writes below land on 200, not on Ink's
                // `VAR Money = 0` default. Without this seed, ANY selection that
                // writes Money (money background, Extra Cash perk, skip-setup)
                // makes the inkjs observer overwrite the observer-synced
                // baseVar.Money from 200 down to its own running total, silently
                // dropping the $200 base. Symptom: picking the +$50 Extra Cash
                // perk took the player from $200 to $50. foundby:曉明 v0.2.7.3.
                story.variablesState['Money'] = variableManager.getValue('Money') || 0;
                switch (bgId) {
                    case 1: // Corporate Burnout
                        story.variablesState['Money'] = (story.variablesState['Money'] || 0) + 100;
                        story.variablesState['max_energy_base_bonus'] = (story.variablesState['max_energy_base_bonus'] || 0) + 5;
                        story.variablesState['max_energy'] = 100 + story.variablesState['max_energy_base_bonus'];
                        break;
                    case 2: // College Dropout
                        story.variablesState['Femininity'] = (story.variablesState['Femininity'] || 0) + 10;
                        story.variablesState['Shame'] = (story.variablesState['Shame'] || 0) + 5;
                        break;
                    case 3: // Gym Bro
                        story.variablesState['max_energy_base_bonus'] = (story.variablesState['max_energy_base_bonus'] || 0) + 15;
                        story.variablesState['max_energy'] = 100 + story.variablesState['max_energy_base_bonus'];
                        story.variablesState['gym_bro_resistance_days'] = 5;
                        break;
                    case 4: // Creative Soul
                        story.variablesState['Femininity'] = (story.variablesState['Femininity'] || 0) + 5;
                        story.variablesState['sissy_followers'] = 10;
                        story.variablesState['perk_ring_light'] = 1;
                        break;
                    case 5: // Shy Introvert
                        story.variablesState['Submissiveness'] = (story.variablesState['Submissiveness'] || 0) + 10;
                        story.variablesState['Arousal'] = (story.variablesState['Arousal'] || 0) + 5;
                        break;
                    case 6: // Sugar Baby
                        story.variablesState['Money'] = (story.variablesState['Money'] || 0) + 150;
                        story.variablesState['Submissiveness'] = (story.variablesState['Submissiveness'] || 0) + 15;
                        story.variablesState['Shame'] = (story.variablesState['Shame'] || 0) + 10;
                        break;
                    case 7: // Military Discipline
                        story.variablesState['max_energy_base_bonus'] = (story.variablesState['max_energy_base_bonus'] || 0) + 20;
                        story.variablesState['max_energy'] = 100 + story.variablesState['max_energy_base_bonus'];
                        story.variablesState['military_resistance_days'] = 7;
                        break;
                }
                console.log(`[PnO] Applied background ${bgId} stat bonuses`);

                // Apply selected perks (matches GameViewController.ApplyPerks)
                if (data.perks && data.perks.length > 0) {
                    for (const perkId of data.perks) {
                        // Set Ink flag for perks that have a matching VAR in Globals.ink
                        try { story.variablesState[perkId] = true; }
                        catch (e) { console.warn(`[Editor] perk flag '${perkId}' missing in Globals.ink:`, e?.message); }
                        try {
                            switch (perkId) {
                                // Stat perks
                                case 'perk_extra_cash':       story.variablesState['Money'] = (story.variablesState['Money'] || 0) + 50; break;
                                case 'perk_fem_head_start':   story.variablesState['Femininity'] = (story.variablesState['Femininity'] || 0) + 5; break;
                                case 'perk_sub_head_start':   story.variablesState['Submissiveness'] = (story.variablesState['Submissiveness'] || 0) + 5; break;
                                case 'perk_energy_boost': {
                                    // Raise the max-energy cap. max_energy is an Ink-computed var
                                    // (get_max_energy()); write the base bonus + set max_energy directly
                                    // on Ink so the bar shows it from Day 1. NOT setValue() — that seeds a
                                    // stale baseVar.max_energy that clobbers later bed/decor recomputes on
                                    // reload (pushVariablesToInk now skips max_energy).
                                    const curBonus = story.variablesState['max_energy_base_bonus'] || 0;
                                    story.variablesState['max_energy_base_bonus'] = curBonus + 10;
                                    story.variablesState['max_energy'] = 100 + curBonus + 10;
                                    break;
                                }
                                // Hardship: Low Stamina — mirror perk_energy_boost
                                // but subtract. max_energy_base_bonus can go
                                // negative; get_max_energy() floors the result.
                                case 'perk_reduced_stamina': {
                                    const curBonus = story.variablesState['max_energy_base_bonus'] || 0;
                                    story.variablesState['max_energy_base_bonus'] = curBonus - 15;
                                    story.variablesState['max_energy'] = 100 + curBonus - 15;
                                    break;
                                }
                                // Gameplay perks
                                // perk_rent_discount: just set the flag (already set generically
                                // at :777). The discount is applied ONGOING at payment time via
                                // rent_due() in Ink — the old `RentAmount -= 25` mutation was
                                // wiped by the first escalation hard-SET and did nothing on the
                                // Independent route (flat $200 deduct ignores RentAmount).
                                case 'perk_rent_discount':    story.variablesState['perk_rent_discount'] = true; break;
                                case 'perk_club_access':      story.variablesState['club_access'] = true; break;
                                case 'perk_extra_rest':       story.variablesState['perk_extra_rest'] = true; break;
                                // perk_dating_bonus: pre-seed one starting match (Tyler — free,
                                // reqFem:10, reachable Day 1) to deliver the advertised "+1
                                // starting match". Ongoing swipe discount lives in phone.js.
                                case 'perk_dating_bonus':     story.variablesState['matched_tyler'] = true; break;
                                // Social perks
                                case 'perk_master_favor': {
                                    // Gate on master_intensity >= 1: on Independent saves the perk would
                                    // leak MR and fire phantom Master-gated content.
                                    const intensity = story.variablesState['master_intensity'] || 0;
                                    if (intensity >= 1) {
                                        const curMR = story.variablesState['MasterRelationship'] || 0;
                                        story.variablesState['MasterRelationship'] = curMR + 2;
                                    }
                                    break;
                                }
                                case 'perk_gf_trust': {
                                    // Sarah Rework v0.2.4: -4 sarah_tension (was -2 girlfriend_suspicion on a 0-5 scale → equivalent on 0-10)
                                    const cur = story.variablesState['sarah_tension'] || 0;
                                    story.variablesState['sarah_tension'] = Math.max(0, cur - 4);
                                    break;
                                }
                                case 'perk_social_butterfly':
                                    // v0.2.8: converted from a one-shot +10 sissy_fame seed to an
                                    // ONGOING fame-rate bonus. The flag (set generically at :777) is
                                    // read by fame_gain() in Globals.ink, granting +1 fame per social
                                    // event for the whole run instead of a single burst at start.
                                    story.variablesState['perk_social_butterfly'] = true;
                                    break;
                                // Streaming perks
                                case 'perk_stream_headstart': story.variablesState['perk_ring_light'] = true; break;
                                case 'perk_stream_setup':     story.variablesState['perk_cute_bg'] = true; break;
                                // 2026-05-07 visible-but-disabled bypass perks (Task #12)
                                case 'perk_early_streamer':
                                    // Player explicitly opted in for streaming from Day 1.
                                    // Pre-populate Knowledge_streaming so the "learn about streaming"
                                    // gate is already cleared. Plus mark streaming-hint as seen so
                                    // the Day-5 SMS doesn't spam them with discovery they bypassed.
                                    story.variablesState['Knowledge_streaming'] = 1;
                                    story.variablesState['sms_streaming_hint'] = true;
                                    break;
                                case 'perk_curious_mind':
                                    // Player explicitly opted in for hypno + dareme. Bypass the
                                    // discovery foreshadowing — they know what they want.
                                    story.variablesState['hypno_content_unlocked'] = true;
                                    story.variablesState['sms_hypno_hint'] = true;
                                    break;
                            }
                        } catch (e) { console.warn(`[Editor] perk apply '${perkId}' failed:`, e?.message); }
                    }
                    console.log(`[PnO] Applied ${data.perks.length} perks with stat bonuses`);
                }

                // Pacing 2.0 (PACING_RETUNE.md): pacing_target_days is the primary knob
                // (slider 35-90, default 60 = Normal). pacing_mode is a DERIVED back-compat
                // bucket (target<=42->2 Fast, <=72->1 Standard, else 0 Slow). If the slider
                // provided a value use it; otherwise map the legacy radio mode to a
                // representative length: Fast(2)->35, Standard(1)->60, Slow(0)->75, VerySlow(3)->90.
                const pacingMode = (typeof data.pacingMode === 'number') ? data.pacingMode : 1;
                const modeToDays = { 2: 35, 1: 60, 0: 75, 3: 90 };
                const pacingTargetDays = (typeof data.pacingTargetDays === 'number')
                    ? Math.max(35, Math.min(90, Math.round(data.pacingTargetDays / 5) * 5))
                    : (modeToDays[pacingMode] ?? 60);
                // 4-way bucket (midpoints of the representative targets 35/60/75/90) so the
                // ~15 legacy pacing_mode==3 branches (phase-lock, honeymoon, spa) stay alive
                // and old saves round-trip (an old mode-3 save migrates to target 90, which
                // derives back to mode 3). The Fast bucket (mode 2) also drives speed_signal.
                const derivedMode = pacingTargetDays <= 47 ? 2
                                  : pacingTargetDays <= 67 ? 1
                                  : pacingTargetDays <= 82 ? 0 : 3;
                story.variablesState['pacing_target_days'] = pacingTargetDays;
                story.variablesState['pacing_mode'] = derivedMode;
                story.variablesState['pacing_migrated'] = true; // new game: skip the old-save migration
                console.log(`[PnO] Pacing: ${pacingTargetDays}d (derived mode ${derivedMode})`);

                // Speed-Signal: bypass phase-wrapper if player explicitly opted for faster pacing
                // via Curious-Trait OR Fem-Head-Start-Perk OR the Fast end of the slider.
                const traits = data.traits || [];
                const perks  = data.perks  || [];
                const hasSpeedSignal = traits.includes('curious')
                                    || perks.includes('perk_fem_head_start')
                                    || derivedMode === 2;
                story.variablesState['speed_signal_active'] = hasSpeedSignal;
                if (hasSpeedSignal) console.log('[PnO] Speed-signal active, phase-wrapper bypassed');
            }
        } catch (e) {
            console.warn('[PnO] Could not set background/traits in Ink:', e.message);
        }

        // Recalculate buffs BEFORE pushing to Ink (sets is_dressed, wearing flags)
        inventoryManager.recalculateAllBuffs();

        // Skip-Setup-Phase: pre-populate VARs so the player starts on Day 8
        // with established routine. Sets a one-shot recap flag the prologue
        // path branches on. Applied AFTER perks / background bonuses so the
        // skip-setup state is the final word.
        if (data.skipSetup) {
            try {
                const story = storyManager.currentStory;
                story.variablesState['Day'] = 8;
                story.variablesState['skip_setup_done'] = true;
                story.variablesState['girlfriend_early_visit'] = true;  // Sarah came over Day 2
                story.variablesState['sarah_honeymoon_done'] = true;    // Saturday visit happened
                story.variablesState['prologue_seen'] = true;           // Skip the prologue narrative
                story.variablesState['downtown_discovered'] = true;     // Player has explored
                story.variablesState['Money'] = (story.variablesState['Money'] || 0) + 200;
                console.log('[PnO] Skip-Setup applied, Day 8 start with established routine');
            } catch (e) {
                console.warn('[PnO] Skip-Setup application failed:', e?.message);
            }
        }

        // MASC_ROUTE §8: the masculine pick seeds a LOW starting Femininity.
        // Background/trait/perk bonuses (College Dropout +10, Creative Soul +5,
        // Fem Head Start +5) would otherwise contradict the picked intent, so
        // cap at 5 as the final word on starting Fem. Direct Ink write — the
        // inkjs observer mirrors it into baseVar before the push below.
        try {
            if ((data.mascPath ?? 0) >= 1) {
                const startFem = storyManager.currentStory.variablesState['Femininity'] || 0;
                if (startFem > 5) {
                    storyManager.currentStory.variablesState['Femininity'] = 5;
                    console.log(`[PnO] Masc pick: starting Femininity capped ${startFem} → 5`);
                }
            }
        } catch (e) { console.warn('[Editor] masc Fem cap failed:', e?.message); }

        // NOW push everything to Ink and start the story
        variableManager.pushVariablesToInk(storyManager.currentStory);

        // Skip-Setup: route directly to the recap-knot instead of the Prologue.
        // The recap prints a 1-message summary of what got skipped, then diverts
        // to Apartment for normal Day-8 morning play.
        if (data.skipSetup) {
            try { storyManager.currentStory.ChoosePathString('Prologue_Skip_Setup_Recap'); }
            catch (e) { console.warn('[PnO] Skip-Setup-Recap divert failed:', e?.message); }
        }
        storyManager.refreshView(false);

        // DEBUG: verify inventory state after init
        console.log('[PnO] Post-init: owned=' + inventoryManager._owned.size +
            ' equipped=' + inventoryManager._equipped.size +
            ' dbLoaded=' + inventoryManager._dbLoaded +
            ' inkPusher=' + !!inventoryManager._inkPusher);
        console.log('[PnO] Owned items:', [...inventoryManager._owned].join(', '));
        console.log('[PnO] is_dressed=' + variableManager.getFlag('is_dressed'));
        try {
            console.log('[PnO] Ink is_dressed=' + storyManager.currentStory.variablesState['is_dressed']);
        } catch(e) {}

        // Register shop items for overlay
        shopUI.registerItems(inventoryManager.getAllItems());
        shopUI.setStoryReader(() => storyManager.currentStory);
        shopUI.setInventoryRef(inventoryManager);

        // Wire appearance system to read Ink vars for image/video variants
        appearanceState.registerInkReader((varName) => {
            try { return storyManager.currentStory?.variablesState[varName]; } catch(e) { return undefined; }
        });

        // Update character panel slot images with starter items
        characterPanelUI.updateSlots();

        // Set player name in character panel header
        const charName = document.getElementById('char-panel-name');
        if (charName && data.name) charName.textContent = data.name;

        // Give phone access to story for reading variables
        phoneUI.setStoryRef(storyManager.currentStory);
        phoneUI.setStoryGetter(() => storyManager.currentStory);

        // Register save collectors for auto-save
        const versionInfo = _opts.getVersionInfo ? _opts.getVersionInfo() : { version: '0.0.0' };
        saveSystem.registerCollectors({
            getStory: () => storyManager.currentStory || null,
            getVariables: () => variableManager.exportState(),
            getInventory: () => inventoryManager.getSerializableState(),
            getCharacterData: () => _opts.getCharacterData ? _opts.getCharacterData() : null,
            getSMS: () => phoneUI.serialize?.()?.threads || [],
            getStatistics: () => statisticsTracker.getSerializableState(),
            getAchievements: () => achievementManager.getUnlockedIds?.() || [],
            getGalleryUnlocked: () => galleryUI.serialize?.() || [],
            getCurrentLocation: () => storyManager.currentLocation || '',
            gameVersion: versionInfo.version,
        });

        // Undo snapshots must carry inventory + phone state (save-equivalent),
        // same registration as main.js bootAndApplySave — keyed, so idempotent.
        registerUndoStateProviders();

        // Close any leftover overlays from previous game session
        layoutManager.closeAllOverlays();

        // Switch to game view
        events.emit(Events.SCREEN_CHANGE, 'screen-game-view');
        console.log('[PnO] New game started');
    } catch (err) {
        console.error('[PnO] Failed to start game:', err);
        renderLoadFailureCard(document.getElementById('story-content'), err);
        events.emit(Events.SCREEN_CHANGE, 'screen-game-view');
    } finally {
        // Always release the re-entrancy guard so a failed start doesn't leave
        // the "Begin Your Story" buttons permanently dead.
        _starting = false;
        startBtns.forEach(b => {
            b.disabled = false;
            b.removeAttribute('aria-disabled');
            b.classList.remove('is-starting');
        });
    }
}

// ── Public API ──

export const characterEditorUI = {
    /**
     * Provide App-level callbacks that can't be imported.
     * @param {object} opts - { setCharacterData, getCharacterData, updateCharacterPanelSlots, getVersionInfo }
     */
    init(opts = {}) {
        _opts = opts;
    },

    /**
     * Build the editor UI (grids, nav buttons).
     */
    bind() {
        // Build background grid
        const bgGrid = document.getElementById('background-grid');
        if (bgGrid) {
            BACKGROUNDS.forEach(bg => {
                const card = createCard(bg, 'background');
                // dataset id so applyDefaultSelections can repaint the selection
                // from editorState (archetype pre-fill entering the wizard).
                card.dataset.bgId = String(bg.id);
                bgGrid.appendChild(card);
            });
            if (BACKGROUNDS.length % 2 === 1) {
                const spacer = document.createElement('div');
                spacer.className = 'card';
                spacer.style.visibility = 'hidden';
                bgGrid.appendChild(spacer);
            }
        }

        // Build trait grid
        const traitGrid = document.getElementById('trait-grid');
        if (traitGrid) {
            TRAITS.forEach(trait => {
                const card = createCard(trait, 'trait');
                card.dataset.traitId = trait.id;
                traitGrid.appendChild(card);
            });
        }

        // Build intensity grid (Phase 3: Master difficulty)
        const intGrid = document.getElementById('intensity-grid');
        if (intGrid) {
            INTENSITIES.forEach(int => {
                const card = document.createElement('div');
                card.className = 'card' + (int.id === editorState.selectedIntensity ? ' selected' : '') + (int.thumb ? ' card-with-thumb' : '');
                card.dataset.intensityId = String(int.id);
                const thumbHtml = int.thumb ? _thumbHtml(int.thumb) : '';
                const l10nPrefix = `intensity.${int.key}`;
                const localName = L.get(`${l10nPrefix}.name`);
                const localDesc = L.get(`${l10nPrefix}.desc`);
                const displayName = localName !== `${l10nPrefix}.name` ? localName : int.name;
                const displayDesc = localDesc !== `${l10nPrefix}.desc` ? localDesc : int.desc;
                const recBadge = int.recommended
                    ? `<span class="card-recommended" data-l10n="editor.recommended">${L.get('editor.recommended') !== 'editor.recommended' ? L.get('editor.recommended') : 'Recommended'}</span>`
                    : '';
                card.innerHTML = `
                    ${thumbHtml}
                    <span class="card-check">&#10003;</span>
                    ${recBadge}
                    <div class="card-icon"><img src="${int.icon}" alt="${int.name}" width="48" height="48"></div>
                    <div class="card-name">${displayName}</div>
                    <div class="card-desc">${displayDesc}</div>
                `;
                card.addEventListener('click', () => {
                    // Picking a Master (Roommate/Landlord/Owner) cancels the masc
                    // route — they're mutually exclusive in Phase 1. Independent
                    // (id 0) leaves the masc pick untouched.
                    if (int.id >= 1 && (editorState.mascPath ?? 0) >= 1) {
                        editorState.mascPath = 0;
                    }
                    editorState.selectedIntensity = int.id;
                    // Update detail text
                    const detailEl = document.getElementById('intensity-detail');
                    if (detailEl) {
                        const localDetail = L.get(`${l10nPrefix}.detail`);
                        detailEl.textContent = localDetail !== `${l10nPrefix}.detail` ? localDetail : int.detail;
                    }
                    _syncMascIntensityCoupling();
                    updateDifficulty();
                });
                _wireCardA11y(card, 'radio');
                intGrid.appendChild(card);
            });
            // Set initial detail text
            const initInt = INTENSITIES.find(i => i.id === editorState.selectedIntensity);
            const detailEl = document.getElementById('intensity-detail');
            if (detailEl && initInt) detailEl.textContent = initInt.detail;
        }

        // Partner cards (0.3.1: Girlfriend / Boyfriend[gated] / No partner) —
        // data lives in editor-data.js PARTNER_OPTIONS (was inline GF_OPTIONS
        // with boolean ids). The boyfriend card render-gates on the v031
        // kill-switch mirror; the odd-count spacer keeps the phone 2-column
        // grid aligned while only 2 cards render.
        const gfGrid = document.getElementById('girlfriend-grid');
        if (gfGrid) {
            const visible = PARTNER_OPTIONS.filter(opt => !opt.gated || _v031On());
            visible.forEach(opt => {
                const card = document.createElement('div');
                card.className = 'card card-with-thumb' + (editorState.partner === opt.id ? ' selected' : '');
                card.dataset.partnerId = opt.id;
                const localName = L.get(`${opt.l10nKey}.name`);
                const localDesc = L.get(`${opt.l10nKey}.desc`);
                card.innerHTML = `
                    ${_thumbHtml(opt.thumb)}
                    <span class="card-check">&#10003;</span>
                    <div class="card-name">${localName !== `${opt.l10nKey}.name` ? localName : opt.name}</div>
                    <div class="card-desc">${localDesc !== `${opt.l10nKey}.desc` ? localDesc : opt.desc}</div>
                `;
                card.addEventListener('click', () => {
                    gfGrid.querySelectorAll('.card').forEach(c => c.classList.remove('selected'));
                    card.classList.add('selected');
                    editorState.partner = opt.id;
                    _updatePartnerOrientationNote();
                });
                _wireCardA11y(card, 'radio');
                gfGrid.appendChild(card);
            });
            if (visible.length % 2 === 1) {
                const spacer = document.createElement('div');
                spacer.className = 'card';
                spacer.style.visibility = 'hidden';
                gfGrid.appendChild(spacer);
            }
        }

        // ── Content Preferences (0.3, CONTENT_TOGGLES). Hidden + dormant unless
        // CONTENT_PREFS_ENABLED (JS mirror of the Ink v030_content_enabled gate).
        // Also hidden entirely for free players when every track is Patreon-only
        // (nothing for them to configure) — no teaser toggle at first launch.
        {
            const cpSection = document.getElementById('content-prefs-section');
            const show = CONTENT_PREFS_ENABLED && hasVisibleContentTracks(!isPat());
            if (cpSection) cpSection.style.display = show ? '' : 'none';
            if (show) {
                buildContentPrefCards(editorState.contentPrefs, '#content-prefs-grid');
                // New-game players meet the opt-in here, so suppress the load-time
                // discovery tip (it targets save-game players who skip the editor).
                try { tutorialTips.markSeen('content_prefs'); } catch (e) {}
            }
        }

        // ── MASC_ROUTE (0.2.8) cards — kill-switch stripped 2026-07-07,
        // MASC_ROUTE_ENABLED stays as a pure JS UI toggle. The masc-route-section
        // reveals and these two grids populate. masc_path / orientation_lean are
        // written to Ink in the post-init block regardless (default 0/0 = inert),
        // so this is purely the picker UI.
        if (MASC_ROUTE_ENABLED) {
            const mascSection = document.getElementById('masc-route-section');
            if (mascSection) mascSection.style.display = '';

            const mpGrid = document.getElementById('masc-path-grid');
            if (mpGrid) {
                MASC_PATH_OPTIONS.forEach(opt => {
                    const card = document.createElement('div');
                    card.className = 'card card-with-thumb' + (editorState.mascPath === opt.id ? ' selected' : '');
                    card.dataset.mascId = String(opt.id);
                    const localName = L.get(`editor.masc_${opt.key}.name`);
                    const localDesc = L.get(`editor.masc_${opt.key}.desc`);
                    card.innerHTML = `
                        ${_thumbHtml(opt.thumb)}
                        <span class="card-check">&#10003;</span>
                        <div class="card-name">${localName !== `editor.masc_${opt.key}.name` ? localName : opt.name}</div>
                        <div class="card-desc">${localDesc !== `editor.masc_${opt.key}.desc` ? localDesc : opt.desc}</div>
                    `;
                    card.addEventListener('click', () => {
                        editorState.mascPath = opt.id;
                        // Two-way coupling: "Stay your own man" forces Independent
                        // + greys the Master cards + shows the note. Selection
                        // classes for both grids are handled by the sync helper.
                        _syncMascIntensityCoupling();
                        updateDifficulty();
                    });
                    _wireCardA11y(card, 'radio');
                    mpGrid.appendChild(card);
                });
                _updateMascRecommended();
                // Reconcile both grids on first render (handles a returning
                // editorState where masc route was already picked).
                _syncMascIntensityCoupling();
            }

            const olGrid = document.getElementById('orientation-lean-grid');
            if (olGrid) {
                ORIENTATION_LEAN_OPTIONS.forEach(opt => {
                    const card = document.createElement('div');
                    card.className = 'card' + (editorState.orientationLean === opt.id ? ' selected' : '');
                    card.dataset.orientId = String(opt.id);
                    const localName = L.get(`editor.orient_${opt.key}.name`);
                    const localDesc = L.get(`editor.orient_${opt.key}.desc`);
                    card.innerHTML = `
                        <span class="card-check">&#10003;</span>
                        <div class="card-name">${localName !== `editor.orient_${opt.key}.name` ? localName : opt.name}</div>
                        <div class="card-desc">${localDesc !== `editor.orient_${opt.key}.desc` ? localDesc : opt.desc}</div>
                    `;
                    card.addEventListener('click', () => {
                        olGrid.querySelectorAll('.card').forEach(c => c.classList.remove('selected'));
                        card.classList.add('selected');
                        editorState.orientationLean = opt.id;
                        _updatePartnerOrientationNote();
                    });
                    _wireCardA11y(card, 'radio');
                    olGrid.appendChild(card);
                });
            }
        }

        // Archetype entry screens (0.3.1). MUST run before the generic
        // [data-action="start-game"] wiring below — the confirm-screen Begin
        // button relies on listener registration order (names copied into the
        // canonical inputs before startNewGame reads them).
        _bindArchetypeUi();

        // Navigation buttons
        document.querySelectorAll('[data-action="next-phase"]').forEach(btn => {
            btn.addEventListener('click', () => editorNextPhase());
        });
        document.querySelectorAll('[data-action="prev-phase"]').forEach(btn => {
            btn.addEventListener('click', () => editorPrevPhase());
        });
        document.querySelectorAll('[data-action="start-game"]').forEach(btn => {
            btn.addEventListener('click', () => startNewGame());
        });

        // Pacing 2.0 story-length slider — live readout (days / weeks / zone /
        // desc) + editorState sync (0.3.1: editorState.pacingTargetDays is the
        // source of truth startNewGame reads; pre-fix the slider bypassed state
        // so getState()/presets couldn't see or set pacing).
        const pacingSlider = document.getElementById('pacing-slider');
        if (pacingSlider) {
            const upd = () => {
                _updatePacingSliderReadout(pacingSlider.value);
                const v = parseInt(pacingSlider.value, 10);
                if (!Number.isNaN(v)) editorState.pacingTargetDays = v;
            };
            pacingSlider.addEventListener('input', upd);
            _updatePacingSliderReadout(pacingSlider.value); // initial paint, no state write
        }

        // Enter-key submit on the name inputs → advance the wizard, so the
        // whole flow is completable from the keyboard.
        ['input-name', 'input-fem-name', 'input-master', 'input-gf'].forEach(id => {
            const input = document.getElementById(id);
            if (input) {
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') { e.preventDefault(); editorNextPhase(); }
                });
            }
        });
    },

    /**
     * Re-apply the visual .selected highlight + dependent readouts from the
     * current editorState. Phase-2 has pre-selected defaults (Roommate +
     * Girlfriend) that bind() only marked once; on editor RE-entry the reset
     * strips the class while keeping the value, leaving those phases with no
     * card highlighted under an active default. Called from _enterCharacterEditor.
     */
    applyDefaultSelections() {
        // Background + traits (0.3.1: archetype pre-fills land here too, so the
        // wizard opens showing a preset as if hand-picked).
        const bgGrid = document.getElementById('background-grid');
        if (bgGrid) {
            bgGrid.querySelectorAll('.card').forEach(c =>
                c.classList.toggle('selected', c.dataset.bgId === String(editorState.selectedBackground)));
        }
        _repaintTraitsFromState();

        const intGrid = document.getElementById('intensity-grid');
        if (intGrid) {
            intGrid.querySelectorAll('.card').forEach(c =>
                c.classList.toggle('selected', c.dataset.intensityId === String(editorState.selectedIntensity)));
        }
        // Intensity detail text for the active default.
        const int = INTENSITIES.find(i => i.id === editorState.selectedIntensity);
        const detailEl = document.getElementById('intensity-detail');
        if (detailEl && int) {
            const k = `intensity.${int.key}.detail`;
            const l = L.get(k);
            detailEl.textContent = l !== k ? l : int.detail;
        }
        const gfGrid = document.getElementById('girlfriend-grid');
        if (gfGrid) {
            gfGrid.querySelectorAll('.card').forEach(c => {
                if (c.dataset.partnerId) c.classList.toggle('selected', c.dataset.partnerId === editorState.partner);
            });
        }
        const mpGrid = document.getElementById('masc-path-grid');
        if (mpGrid) {
            mpGrid.querySelectorAll('.card').forEach(c =>
                c.classList.toggle('selected', c.dataset.mascId === String(editorState.mascPath)));
        }
        const olGrid = document.getElementById('orientation-lean-grid');
        if (olGrid) {
            olGrid.querySelectorAll('.card').forEach(c =>
                c.classList.toggle('selected', c.dataset.orientId === String(editorState.orientationLean)));
        }
        // Masc coupling repaint (locked classes + note) — a masc preset entering
        // the wizard must show the greyed Master cards exactly like a hand-pick.
        _syncMascIntensityCoupling();
        _updateMascRecommended();
        _updatePartnerOrientationNote();
        // Content-pref rows: REBUILD so the toggle closures capture the CURRENT
        // editorState.contentPrefs array. bind() wired them once at boot; after
        // any editorState replacement (reset / archetype apply) the old closures
        // would mutate a stale array and pref_* writes silently stop.
        {
            const cpSection = document.getElementById('content-prefs-section');
            const show = CONTENT_PREFS_ENABLED && hasVisibleContentTracks(!isPat());
            if (cpSection) cpSection.style.display = show ? '' : 'none';
            if (show) buildContentPrefCards(editorState.contentPrefs, '#content-prefs-grid');
        }
        // Pacing slider mirrors editorState (preset pacing survives customize).
        const slider = document.getElementById('pacing-slider');
        if (slider && typeof editorState.pacingTargetDays === 'number') {
            slider.value = String(editorState.pacingTargetDays);
            _updatePacingSliderReadout(slider.value);
        }
        _setBackgroundGate();
        updateDifficulty();
        _refreshAriaChecked();
    },

    /** Show the archetype quick-start entry screen (New Game landing, 0.3.1).
     * Called from main.js _enterCharacterEditor after resetState(). */
    showEntry,

    startNewGame,

    getState() {
        return { ...editorState };
    },

    resetState() {
        editorState = _defaultEditorState();
    },

    /** Programmatic quick start with the recommended default character. The
     * main-menu button was removed in 0.3.1 (the archetype screen IS the quick
     * start now); this stays as the API tests + tooling drive via
     * window._charEditor. Derives every axis from the `recommended: true`
     * flags in editor-data.js — same semantics as the shipped button had, so
     * the test-baseline balance is unchanged. */
    async quickStart() {
        const recBg = BACKGROUNDS.find(b => b.recommended);
        const recInt = INTENSITIES.find(i => i.recommended);
        const recOri = ORIENTATION_LEAN_OPTIONS.find(o => o.recommended);
        editorState = {
            ..._defaultEditorState(),
            selectedBackground: recBg ? recBg.id : 1, // Corporate Burnout
            selectedIntensity: recInt ? recInt.id : 1, // Roommate
            orientationLean: recOri ? recOri.id : 0, // Open
        };
        await startNewGame();
    },
};
